import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { FormControl } from '@angular/forms';

import { EoFileUploadConfig } from '../../form';
import { EoDatepickerConfig } from '../../form/eo-date-picker/eo-date-picker.model';
import { EoDropdownConfig } from '../../form/eo-dropdown/eo-dropdown.model';
import { EoFormPanelConfig } from '../../form/eo-form-panel/eo-form-panel.model';
import {
  EoFormConfig,
  EoFormControlConfig,
  IEoFormControlConfig,
} from '../../form/eo-form/eo-form.config';
import { EoInputConfig } from '../../form/eo-input/eo-input.model';
import { EoMultiselectConfig } from '../../form/eo-multiselect/eo-multiselect.model';
import { EoFormDialogComponent } from '../eo-form-dialog/eo-form-dialog.component';
import { EoFormTableService } from './eo-form-table.service';
import { IEoFormTableConfig } from './model/eo-form-table-config.model';
import { IFormField } from './model/form-field.model';

@Component({
  selector: 'eo-form-table',
  templateUrl: './eo-form-table.component.html',
  styleUrls: ['./eo-form-table.component.scss'],
})
export class EoFormTableComponent {
  // @ViewChild('eoForm') eoForm?: EoFormComponent;
  @ViewChild('eoFormDialog') eoFormDialog?: EoFormDialogComponent;
  formTableService = inject(EoFormTableService);

  private _config?: IEoFormTableConfig;
  @Input() set config(val: IEoFormTableConfig | undefined) {
    this._config = val;
    this.setFormConfig();
  }
  get config(): IEoFormTableConfig | undefined {
    return this._config;
  }

  get createButtonDisabled(): boolean {
    if (!this.config) return false;
    if (
      this.config.maxEntries != undefined &&
      this.config.tableData != undefined &&
      this.config.maxEntries <= this.config.tableData.length
    )
      return true;

    return false;
  }

  get valid(): boolean {
    if (
      this.config != undefined &&
      this.config.minEntries != undefined &&
      this.config.tableData != undefined &&
      this.config.minEntries > this.config.tableData.length
    )
      return false;

    if (
      this.config != undefined &&
      this.config.maxEntries != undefined &&
      this.config.tableData != undefined &&
      this.config.maxEntries < this.config.tableData.length
    )
      return false;

    if (
      this.config != undefined &&
      this.config.requiredNumberOfEntries != undefined &&
      this.config.tableData != undefined &&
      this.config.requiredNumberOfEntries != this.config.tableData.length
    )
      return false;

    return true;
  }

  _submitLoading?: boolean;
  @Input() set submitLoading(val: boolean) {
    this._submitLoading = val;
  }
  get submitLoading(): boolean {
    if (this._submitLoading !== undefined) return this._submitLoading;
    if (typeof this.config?.submitLoading === 'function') {
      const data = this.eoFormDialog?.eoForm?.getFormData();
      return this.config.submitLoading(data);
    }
    return this.config?.submitLoading === true || false;
  }

  _deleteLoading?: boolean;
  @Input() set deleteLoading(val: boolean) {
    this._deleteLoading = val;
  }
  get deleteLoading(): boolean {
    if (this._deleteLoading !== undefined) return this._deleteLoading;
    if (typeof this.config?.deleteLoading === 'function') {
      const data = this.eoFormDialog?.eoForm?.getFormData();
      return this.config.deleteLoading(data);
    }
    return this.config?.deleteLoading === true || false;
  }

  @Output() onSubmit = new EventEmitter<any>();
  @Output() onDeleteItem = new EventEmitter<any>();

  formConfig?: EoFormConfig;
  formDialogVisible = false;

  onFetchData(): void {
    if (!this.config?.fetchApiUrl) return;

    this.formTableService
      .fetch(this.config.fetchApiUrl)
      .subscribe((data) => this.onFetchDataSuccess(data));
  }

  onFetchDataSuccess(data: any[]) {
    if (!this.config) return;
    this.config.tableData = data;
  }

  onCreateButtonClick(): void {
    this.formDialogVisible = true;
  }

  onFormSubmit(formData: any): void {
    const data = this.eoFormDialog?.eoForm?.getFormData();
    this.config?.onSubmit?.(data);
    this.onSubmit.emit(data);
  }

  onCreateNewSuccess(): void {
    this.formDialogVisible = false;
  }

  onDelete(item: any): void {
    this.config?.onDelete?.(item);
    this.onDeleteItem.emit(item);
  }

  onDeleteSuccess(data: any): void {
    // remove data from array
  }

  setFormConfig(): void {
    this.formConfig = new EoFormConfig({
      id: this.config!.formId,
      config: [
        new EoFormPanelConfig({
          name: 'form',
          styleClass: 'grid',
          config: this.getFormControls(this.config!.formFields!),
        }),
      ],
    });

    if (this.eoFormDialog?.eoForm)
      this.eoFormDialog.eoForm.config = this.formConfig;
  }

  getFormControls(fields: IFormField[]): IEoFormControlConfig[] {
    let formControls: IEoFormControlConfig[] = [];

    fields.forEach((field) => {
      let fcConfig: IEoFormControlConfig | undefined;
      switch (field.type) {
        case 'string':
          fcConfig = new EoInputConfig({
            ...this.getEoFormControlConfig(field),
            type: 'text',
          });
          break;
        case 'dropdown':
          fcConfig = new EoDropdownConfig({
            ...this.getEoFormControlConfig(field),
            optionLabel: field.optionLabel,
            optionValue: field.optionValue,
            apiUrl: field.url,
          });
          break;
        case 'multiselect':
          fcConfig = new EoMultiselectConfig({
            ...this.getEoFormControlConfig(field),
            optionLabel: field.optionLabel,
            optionValue: field.optionValue,
            apiUrl: field.url,
          });
          break;
        case 'dateTo':
          fcConfig = new EoDatepickerConfig({
            ...this.getEoFormControlConfig(field),
            appendTo: 'body',
          });
          break;
        case 'dateFrom':
          fcConfig = new EoDatepickerConfig({
            ...this.getEoFormControlConfig(field),
            appendTo: 'body',
          });
          break;
        case 'date':
          fcConfig = new EoDatepickerConfig({
            ...this.getEoFormControlConfig(field),
            minDate: field.minValue as Date | undefined,
            maxDate: field.maxValue as Date | undefined,
            appendTo: 'body',
          });
          break;
        case 'number':
          fcConfig = new EoInputConfig({
            ...this.getEoFormControlConfig(field),
            type: 'number',
          });
          break;
        case 'fileUpload':
          fcConfig = new EoFileUploadConfig({
            ...this.getEoFormControlConfig(field),
          });
          break;
        case 'hidden':
          fcConfig = new EoInputConfig({
            name: field.fieldId,
            styleClass: 'hidden',
            control: new FormControl('-'),
          });
          break;
      }

      if (fcConfig) formControls.push(fcConfig);
    });

    return formControls;
  }

  getEoFormControlConfig(field: IFormField): EoFormControlConfig {
    return {
      name: field.fieldId,
      styleClass: 'col-12',
      label: field.label,
      control: new FormControl(null, [...field.validators]),
    };
  }

  getFileUrl(item: any, propFieldId: string): string {
    return item?.[propFieldId]?.fileUrl ?? '';
  }

  getFileName(item: any, propFieldId: string): string {
    return item?.[propFieldId]?.fileName ?? '';
  }
}
